import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from '@mui/material';
import { buttonStyles } from './css/commonStyles';
import {
  colors,
  newRequestBtnSx,
  pageShellStyles,
  subTabSx,
  tabHeaderRowSx,
  tableAlertErrorSx,
  tableCardStyles,
  tableContainerStyles,
  pageTitleSx,
  vaultWarningBannerSx,
} from './css/revenueItems.styles';
import { buildPaginationItems } from './utils/revenueItems.utils';
import { getPaymentRequests, getRevenueItems } from 'helpers/paymentRequest.api';
import config from 'config';

import ManagementTab from './components/ManagementTab';
import CreatePaymentDialog from './components/CreatePaymentDialog';
import PaymentRequestRow from './components/PaymentRequestRow';
import RevenueItemRow from './components/RevenueItemRow';
import PaymentsPagination from './components/PaymentsPagination';
import CopyLinkToast from './components/CopyLinkToast';
import SuccessToast from './components/SuccessToast';

const RevenueItemsTab = ({ currentDao, onRefreshDao }) => {
  // ─── Permission ───────────────────────────────────────────────────────────────
  const [isOrganizer, setIsOrganizer] = useState(false);

  useEffect(() => {
    const check = () => {
      if (window.ethereum?.selectedAddress && currentDao?.organizerAddress) {
        setIsOrganizer(window.ethereum.selectedAddress.toLowerCase() === currentDao.organizerAddress.toLowerCase());
      } else {
        setIsOrganizer(false);
      }
    };
    check();
    if (window.ethereum) {
      window.ethereum.on('accountsChanged', check);
      return () => window.ethereum.removeListener('accountsChanged', check);
    }
  }, [currentDao?.organizerAddress]);

  // ─── Tab state ────────────────────────────────────────────────────────────────
  const [subTab, setSubTab] = useState(0);

  // ─── Dialog / toast state ─────────────────────────────────────────────────────
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [toast, setToast] = useState({ open: false, paymentUrl: '' });
  const [copyToast, setCopyToast] = useState(false);

  // ─── Payment list state ───────────────────────────────────────────────────────
  const [paymentRequests, setPaymentRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, totalItems: 0, totalPages: 1 });
  const [refreshKey, setRefreshKey] = useState(0);

  const [revenueItems, setRevenueItems] = useState([]);
  const [revenueLoading, setRevenueLoading] = useState(false);
  const [revenueError, setRevenueError] = useState('');
  const [revenuePage, setRevenuePage] = useState(1);
  const [revenuePagination, setRevenuePagination] = useState({
    page: 1,
    limit: 20,
    totalItems: 0,
    totalPages: 1,
  });

  const daoName = currentDao?.name || '';

  // ─── Effects ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    setPage(1);
    setRevenuePage(1);
  }, [currentDao?._id]);

  useEffect(() => {
    let isMounted = true;
    const fetch = async () => {
      setIsLoading(true);
      setError('');
      try {
        const res = await getPaymentRequests({ page, limit: 20, sortBy: 'desc', daoId: currentDao?._id });
        if (!isMounted) return;
        setPaymentRequests(res?.data || []);
        setPagination({
          page: res?.pagination?.page || page,
          limit: res?.pagination?.limit || 20,
          totalItems: res?.pagination?.totalItems || 0,
          totalPages: res?.pagination?.totalPages || 1,
        });
      } catch (err) {
        if (!isMounted) return;
        setPaymentRequests([]);
        setPagination({ page: 1, limit: 20, totalItems: 0, totalPages: 1 });
        setError(err?.message || 'Failed to load payment requests.');
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };
    fetch();
    return () => {
      isMounted = false;
    };
  }, [currentDao?._id, page, refreshKey]);

  useEffect(() => {
    if (subTab !== 1 || !currentDao?._id) return undefined;

    let isMounted = true;
    const fetchRevenue = async () => {
      setRevenueLoading(true);
      setRevenueError('');
      try {
        const res = await getRevenueItems({
          page: revenuePage,
          limit: 20,
          sortOrder: 'desc',
          daoId: currentDao._id,
          ...(currentDao.chainId != null && currentDao.chainId !== ''
            ? { chainId: String(currentDao.chainId) }
            : {}),
        });
        if (!isMounted) return;
        setRevenueItems(res?.data || []);
        setRevenuePagination({
          page: res?.pagination?.page || revenuePage,
          limit: res?.pagination?.limit || 20,
          totalItems: res?.pagination?.totalItems || 0,
          totalPages: res?.pagination?.totalPages || 1,
        });
      } catch (err) {
        if (!isMounted) return;
        setRevenueItems([]);
        setRevenuePagination({ page: 1, limit: 20, totalItems: 0, totalPages: 1 });
        setRevenueError(err?.message || 'Failed to load revenue items.');
      } finally {
        if (isMounted) setRevenueLoading(false);
      }
    };
    fetchRevenue();
    return () => {
      isMounted = false;
    };
  }, [subTab, currentDao?._id, revenuePage]);

  // ─── Memos ────────────────────────────────────────────────────────────────────
  const paginationItems = useMemo(
    () => buildPaginationItems(pagination.page || 1, pagination.totalPages || 1),
    [pagination.page, pagination.totalPages],
  );

  const paginationLabel = useMemo(() => {
    if (!pagination.totalItems) return isLoading ? 'Loading payment requests...' : 'No payment requests found';
    const start = (pagination.page - 1) * pagination.limit + 1;
    const end = Math.min(start + paymentRequests.length - 1, pagination.totalItems);
    return `Showing ${start}-${end} of ${pagination.totalItems} payment requests`;
  }, [isLoading, pagination, paymentRequests.length]);

  const revenuePaginationItems = useMemo(
    () => buildPaginationItems(revenuePagination.page || 1, revenuePagination.totalPages || 1),
    [revenuePagination.page, revenuePagination.totalPages],
  );

  const revenuePaginationLabel = useMemo(() => {
    if (!revenuePagination.totalItems) {
      return revenueLoading ? 'Loading revenue items...' : 'No revenue items found';
    }
    const start = (revenuePagination.page - 1) * revenuePagination.limit + 1;
    const end = Math.min(start + revenueItems.length - 1, revenuePagination.totalItems);
    return `Showing ${start}-${end} of ${revenuePagination.totalItems} revenue items`;
  }, [revenueLoading, revenuePagination, revenueItems.length]);

  // ─── Handlers ─────────────────────────────────────────────────────────────────
  const handleCopyPaymentUrl = (memoHash) => {
    if (!memoHash) return;
    const url = `${config.sites?.kambria || window.location.origin}/pay?memoHash=${memoHash}`;
    navigator.clipboard.writeText(url);
    setCopyToast(true);
  };

  const handleCreateSuccess = (paymentUrl) => {
    setRefreshKey((k) => k + 1);
    setToast({ open: true, paymentUrl });
  };

  // ─── Table body ───────────────────────────────────────────────────────────────
  const renderTableBody = () => {
    if (isLoading) {
      return (
        <TableRow>
          <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1.5 }}>
              <CircularProgress size={20} sx={{ color: colors.accent }} />
              <Typography sx={{ color: colors.textSecondary }}>Loading payment requests...</Typography>
            </Box>
          </TableCell>
        </TableRow>
      );
    }
    if (paymentRequests.length) {
      return paymentRequests.map((item) => (
        <PaymentRequestRow key={item._id} item={item} daoName={daoName} onCopyPaymentUrl={handleCopyPaymentUrl} />
      ));
    }
    return (
      <TableRow>
        <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
          <Typography sx={{ color: colors.textSecondary }}>No payment requests found.</Typography>
        </TableCell>
      </TableRow>
    );
  };

  const renderRevenueTableBody = () => {
    if (revenueLoading) {
      return (
        <TableRow>
          <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1.5 }}>
              <CircularProgress size={20} sx={{ color: colors.accent }} />
              <Typography sx={{ color: colors.textSecondary }}>Loading revenue items...</Typography>
            </Box>
          </TableCell>
        </TableRow>
      );
    }
    if (revenueItems.length) {
      return revenueItems.map((item) => (
        <RevenueItemRow key={item._id} item={item} daoName={daoName} />
      ));
    }
    return (
      <TableRow>
        <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
          <Typography sx={{ color: colors.textSecondary }}>No revenue items found.</Typography>
        </TableCell>
      </TableRow>
    );
  };

  // ─── Permission gate ──────────────────────────────────────────────────────────
  if (!isOrganizer) {
    return (
      <Box textAlign="center" p={3}>
        <Typography variant="body1" sx={{ color: '#fcfcfc' }}>
          You do not have permission to access this content. Only the DAO organizer can access this section.
        </Typography>
      </Box>
    );
  }

  // ─── Main render ──────────────────────────────────────────────────────────────
  return (
    <Box sx={pageShellStyles}>
      <Tabs value={subTab} onChange={(e, v) => setSubTab(v)} sx={subTabSx}>
        <Tab label="Payment requests" />
        <Tab label="Revenue items" />
        <Tab label="Management" />
      </Tabs>

      {subTab === 2 && <ManagementTab currentDao={currentDao} onSaveSuccess={onRefreshDao} />}

      {subTab === 0 && (
        <>
          <Box sx={tabHeaderRowSx}>
            <Typography variant="h6" sx={pageTitleSx}>
              Payment requests
            </Typography>
            <Button
              variant="contained"
              onClick={() => setIsCreateDialogOpen(true)}
              disabled={!currentDao?.vaultAddress}
              sx={{ ...buttonStyles, ...newRequestBtnSx }}
            >
              New Request
            </Button>
          </Box>

          {error && (
            <Alert severity="error" sx={tableAlertErrorSx}>
              {error}
            </Alert>
          )}

          {!currentDao?.vaultAddress && (
            <Box sx={vaultWarningBannerSx}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" style={{ flexShrink: 0 }}>
                  <path d="M10 2L18.66 17H1.34L10 2Z" stroke="#eab308" strokeWidth="1.5" strokeLinejoin="round" />
                  <path d="M10 8v4" stroke="#eab308" strokeWidth="1.5" strokeLinecap="round" />
                  <circle cx="10" cy="14.5" r="0.75" fill="#eab308" />
                </svg>
                <Typography sx={{ color: '#eab308', fontFamily: 'Questrial, sans-serif', fontSize: '1rem' }}>
                  Vault address is not configured. Please set it up before creating payment requests.
                </Typography>
              </Box>
              <Button
                variant="outlined"
                onClick={() => setSubTab(2)}
                sx={{
                  color: '#eab308',
                  borderColor: '#eab308',
                  borderRadius: '10px',
                  textTransform: 'none',
                  fontFamily: 'Questrial, sans-serif',
                  fontSize: '0.9rem',
                  whiteSpace: 'nowrap',
                  '&:hover': { borderColor: '#eab308', backgroundColor: 'rgba(234,179,8,0.1)' },
                }}
              >
                Update Vault Address
              </Button>
            </Box>
          )}

          <Box sx={tableCardStyles}>
            <TableContainer component={Box} sx={tableContainerStyles}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ minWidth: 120 }}>DAO</TableCell>
                    <TableCell sx={{ minWidth: 170 }}>Revenue Type</TableCell>
                    <TableCell sx={{ minWidth: 150 }}>Amount</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell sx={{ minWidth: 170 }}>Memo Hash</TableCell>
                    <TableCell sx={{ minWidth: 150 }}>External Ref</TableCell>
                    <TableCell sx={{ minWidth: 150 }}>Created Date</TableCell>
                    <TableCell sx={{ minWidth: 150 }}>Expired At</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>{renderTableBody()}</TableBody>
              </Table>
            </TableContainer>

            <PaymentsPagination
              pagination={pagination}
              paginationItems={paginationItems}
              paginationLabel={paginationLabel}
              onPageChange={setPage}
            />
          </Box>

          <CreatePaymentDialog
            open={isCreateDialogOpen}
            onClose={() => setIsCreateDialogOpen(false)}
            currentDao={currentDao}
            onSuccess={handleCreateSuccess}
          />

          <CopyLinkToast open={copyToast} onClose={() => setCopyToast(false)} />

          <SuccessToast
            open={toast.open}
            paymentUrl={toast.paymentUrl}
            onClose={() => setToast({ open: false, paymentUrl: '' })}
          />
        </>
      )}

      {subTab === 1 && (
        <>
          <Box sx={tabHeaderRowSx}>
            <Typography variant="h6" sx={pageTitleSx}>
              Revenue items
            </Typography>
          </Box>

          {revenueError && (
            <Alert severity="error" sx={tableAlertErrorSx}>
              {revenueError}
            </Alert>
          )}

          <Box sx={tableCardStyles}>
            <TableContainer component={Box} sx={tableContainerStyles}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ minWidth: 120 }}>DAO</TableCell>
                    <TableCell sx={{ minWidth: 130 }}>Revenue Type</TableCell>
                    <TableCell sx={{ minWidth: 130 }}>Source</TableCell>
                    <TableCell sx={{ minWidth: 120 }}>Amount</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell sx={{ minWidth: 160 }}>Memo Hash</TableCell>
                    <TableCell sx={{ minWidth: 160 }}>Tx Hash</TableCell>
                    <TableCell sx={{ minWidth: 160 }}>From Address</TableCell>
                    <TableCell sx={{ minWidth: 150 }}>Created</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>{renderRevenueTableBody()}</TableBody>
              </Table>
            </TableContainer>

            <PaymentsPagination
              pagination={revenuePagination}
              paginationItems={revenuePaginationItems}
              paginationLabel={revenuePaginationLabel}
              onPageChange={setRevenuePage}
            />
          </Box>
        </>
      )}
    </Box>
  );
};

export default RevenueItemsTab;
